/**
 * The wire contract for "this host is not provisioned for your tenant".
 *
 * Shared by the API handlers that raise it and the client code that reacts to it, so the
 * two cannot drift. It is deliberately its own answer rather than a flavour of 500 or of
 * 403: a 500 is retried (this can never succeed on a retry) and a 403 is cached as a real
 * permissions verdict (this is a statement about the HOST, not the account).
 */

/** Machine-readable marker in the JSON body. The client branches on THIS, not the status. */
export const TENANT_NOT_CONFIGURED_CODE = 'TENANT_NOT_CONFIGURED';

/**
 * 409 Conflict: the request is well-formed and the credential is valid, but it conflicts
 * with the state of this host — it serves a set of tenants, and the caller's is not in it.
 *
 * Not 500: nothing failed and nothing is going to succeed on a retry, so the client must
 * stop retrying. Not 401: the session is perfectly good and signing in again cannot help.
 * Not 403: that means "you may not have this", which the client renders as "no apps
 * assigned to your account" — a statement about the ACCOUNT, and this is a statement about
 * the HOST. The account is fine; it is standing in the wrong place.
 */
export const TENANT_NOT_CONFIGURED_STATUS = 409;

/** The shape those handlers return, so the client's narrowing has something to match. */
export interface TenantNotConfiguredBody {
  success: false;
  code: typeof TENANT_NOT_CONFIGURED_CODE;
  error: string;
  /** The tenant this host could not serve. Named so support can act without a log dive. */
  tenant: string;
}

/**
 * Does this response body say the host cannot serve the caller's tenant?
 *
 * Reads the body rather than the status because a status alone is ambiguous — a proxy or
 * a future handler could 409 for its own reasons — and acting on that would show the
 * wrong card. The code is only ever set by the helper that builds this answer.
 */
export function isTenantNotConfigured(body: unknown): body is TenantNotConfiguredBody {
  return (
    typeof body === 'object' &&
    body !== null &&
    (body as { code?: unknown }).code === TENANT_NOT_CONFIGURED_CODE
  );
}
